import { Formatters, IntlMessageFormat } from "intl-messageformat";

export type MessageArgs = Readonly<
  Record<string, string | number | boolean | Date | null | undefined>
>;

const memoise = <TArgs extends unknown[], TResult>(
  create: (...args: TArgs) => TResult
): ((...args: TArgs) => TResult) => {
  const cache = new Map<string, TResult>();
  return (...args) => {
    const key = JSON.stringify(args);
    let value = cache.get(key);
    if (value === undefined) {
      value = create(...args);
      cache.set(key, value);
    }
    return value;
  };
};

const formatters: Formatters = {
  getNumberFormat: memoise(
    (locales, options) =>
      // FormatJS types useGrouping wider than the TypeScript 5.3 lib does.
      new Intl.NumberFormat(
        locales,
        options as Intl.NumberFormatOptions | undefined
      )
  ),
  getDateTimeFormat: memoise(
    (locales, options) => new Intl.DateTimeFormat(locales, options)
  ),
  // intl-messageformat throws on a missing global Intl.PluralRules before it
  // ever calls this, so a fallback here would be unreachable; see
  // assertPluralRulesAvailable for what a host without it must do instead.
  getPluralRules: memoise(
    (locales, options) => new Intl.PluralRules(locales, options)
  ),
};

/**
 * Checked when copy is created rather than when a plural first renders, so a
 * runtime without Intl.PluralRules fails on the first useCopy with the fix
 * named, not on whichever screen happens to format a plural.
 */
export const assertPluralRulesAvailable = (): void => {
  if (typeof Intl.PluralRules !== "function") {
    throw new Error(
      'Intl.PluralRules is missing from this runtime: import "@ifgengineering/copy-catalogue/polyfill" before the app.'
    );
  }
};

const compiled = new Map<string, IntlMessageFormat>();

const compile = (message: string, locale: string): IntlMessageFormat => {
  const cacheKey = `${locale}|${message}`;
  let format = compiled.get(cacheKey);
  if (!format) {
    format = new IntlMessageFormat(message, locale, undefined, { formatters });
    compiled.set(cacheKey, format);
  }
  return format;
};

/** Formats one ICU message. Messages carry no markup, so the result is text. */
export const formatMessage = (
  message: string,
  locale: string,
  args?: MessageArgs
): string => {
  const result = compile(message, locale).format(
    args as Record<string, unknown> | undefined
  );
  if (typeof result === "string") return result;
  return Array.isArray(result) ? result.map(String).join("") : String(result);
};
